import React, { useEffect, useMemo, useState } from 'react';
import { Image as ImageIcon, Loader2, Save, Upload } from 'lucide-react';
import { useSchool } from '../../../context/SchoolContext';
import SettingsPageHeader from '../components/SettingsPageHeader';
import ToastMessage from '../components/ToastMessage';
import { useToast } from '../hooks/useToast';
import { schoolSettingsApi } from '../services/schoolSettingsApi';
import {
  getApiErrorMessage,
  resolveAssetUrl,
} from '../services/settingsResponse';

const fieldClass =
  'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100';

const FileCard = ({ label, description, value, preview, onChange }) => (
  <div className="rounded-xl border border-gray-200 p-4">
    <div className="flex items-start gap-4">
      <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
        {preview || value ? (
          <img
            src={preview || resolveAssetUrl(value)}
            alt={`${label} preview`}
            className="h-full w-full object-contain"
          />
        ) : (
          <ImageIcon className="h-7 w-7 text-gray-300" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-gray-900">{label}</h3>
        <p className="mt-1 text-xs leading-5 text-gray-500">{description}</p>
        <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50">
          <Upload className="h-4 w-4" />
          Choose image
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(event) => onChange(event.target.files?.[0] || null)}
          />
        </label>
      </div>
    </div>
  </div>
);

const BrandingSettingsPage = () => {
  const {
    schoolId,
    branding,
    brandingLoading,
    setBranding,
    refreshBranding,
  } = useSchool();
  const [colors, setColors] = useState({
    primary_color: '#2563EB',
    secondary_color: '#0F172A',
    receipt_footer_text: '',
  });
  const [files, setFiles] = useState({
    logo: null,
    seal: null,
    principal_signature: null,
  });
  const [saving, setSaving] = useState(false);
  const { toast, showToast, clearToast } = useToast();

  useEffect(() => {
    if (branding) {
      setColors({
        primary_color: branding.primary_color || '#2563EB',
        secondary_color: branding.secondary_color || '#0F172A',
        receipt_footer_text: branding.receipt_footer_text || '',
      });
    }
  }, [branding]);

  useEffect(() => {
    if (schoolId && !branding) {
      refreshBranding().catch((error) =>
        showToast(getApiErrorMessage(error, 'Failed to load branding.'), 'error'),
      );
    }
  }, [schoolId, branding, refreshBranding, showToast]);

  const previews = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(files).map(([key, file]) => [
          key,
          file ? URL.createObjectURL(file) : '',
        ]),
      ),
    [files],
  );

  useEffect(
    () => () => {
      Object.values(previews).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    },
    [previews],
  );

  const handleSave = async (event) => {
    event.preventDefault();
    if (!schoolId) {
      showToast('School ID is missing from the logged-in user.', 'error');
      return;
    }

    const formData = new FormData();
    Object.entries(colors).forEach(([key, value]) => formData.append(key, value || ''));
    Object.entries(files).forEach(([key, file]) => {
      if (file) formData.append(key, file);
    });

    setSaving(true);
    try {
      const updated = await schoolSettingsApi.updateBranding(schoolId, formData);
      setBranding(updated);
      setFiles({ logo: null, seal: null, principal_signature: null });
      showToast('School branding updated successfully.');
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to update branding.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (brandingLoading && !branding) {
    return (
      <div className="flex min-h-[360px] items-center justify-center text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading branding...
      </div>
    );
  }

  return (
    <>
      <ToastMessage toast={toast} onClose={clearToast} />
      <SettingsPageHeader
        title="Branding"
        description="Upload school assets and configure document colours. The sidebar uses the saved logo and school name."
      />

      <form onSubmit={handleSave} className="space-y-7">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <FileCard
            label="School logo"
            description="Used in the sidebar, ID cards, receipts and reports."
            value={branding?.logo}
            preview={previews.logo}
            onChange={(file) => setFiles((current) => ({ ...current, logo: file }))}
          />
          <FileCard
            label="School seal"
            description="Used on official school documents where supported."
            value={branding?.seal}
            preview={previews.seal}
            onChange={(file) => setFiles((current) => ({ ...current, seal: file }))}
          />
          <FileCard
            label="Principal signature"
            description="Used on generated documents and cards where supported."
            value={branding?.principal_signature}
            preview={previews.principal_signature}
            onChange={(file) =>
              setFiles((current) => ({ ...current, principal_signature: file }))
            }
          />
        </div>

        <div className="grid grid-cols-1 gap-5 border-t border-gray-200 pt-7 md:grid-cols-2">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Primary colour</span>
            <div className="mt-1 flex items-center gap-3">
              <input
                type="color"
                value={colors.primary_color}
                onChange={(event) =>
                  setColors((current) => ({ ...current, primary_color: event.target.value }))
                }
                className="h-11 w-14 cursor-pointer rounded border border-gray-300 bg-white p-1"
              />
              <input
                value={colors.primary_color}
                onChange={(event) =>
                  setColors((current) => ({ ...current, primary_color: event.target.value }))
                }
                className={fieldClass}
              />
            </div>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Secondary colour</span>
            <div className="mt-1 flex items-center gap-3">
              <input
                type="color"
                value={colors.secondary_color}
                onChange={(event) =>
                  setColors((current) => ({ ...current, secondary_color: event.target.value }))
                }
                className="h-11 w-14 cursor-pointer rounded border border-gray-300 bg-white p-1"
              />
              <input
                value={colors.secondary_color}
                onChange={(event) =>
                  setColors((current) => ({ ...current, secondary_color: event.target.value }))
                }
                className={fieldClass}
              />
            </div>
          </label>
          {/* <label className="block md:col-span-2">
            <span className="text-sm font-medium text-gray-700">Receipt footer text</span>
            <textarea
              rows="4"
              value={colors.receipt_footer_text}
              onChange={(event) =>
                setColors((current) => ({
                  ...current,
                  receipt_footer_text: event.target.value,
                }))
              }
              placeholder="Thank you for your payment."
              className={fieldClass}
            />
          </label> */}
        </div>

        <div className="flex justify-end border-t border-gray-200 pt-5">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save branding
          </button>
        </div>
      </form>
    </>
  );
};

export default BrandingSettingsPage;
